import { useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { useBriefings, useLatestBriefings } from '../lib/data-service'
import type { BriefingListItem, Briefing } from '../lib/data-service'
import GlobalHeader from '../components/GlobalHeader'
import {
  Newspaper,
  BookOpen,
  Calendar,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  ChevronDown,
  Loader2,
  MessageSquare,
} from 'lucide-react'

type FilterTab = 'all' | 'daily' | 'weekly'

function parseDate(dateStr: string): Date {
  // Handle both "2026-02-22" and "2026-02-22T00:00:00.000Z" formats
  const ymd = dateStr.slice(0, 10)
  return new Date(ymd + 'T12:00:00')
}

function formatDateRange(start: string, end: string): string {
  const s = parseDate(start)
  const e = parseDate(end)
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  if (start.slice(0, 10) === end.slice(0, 10)) {
    // Same day (daily briefing) — show single date with year
    return s.toLocaleDateString('en-US', { ...opts, year: 'numeric' })
  }
  const sStr = s.toLocaleDateString('en-US', opts)
  const eStr = e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })
  return `${sStr} \u2013 ${eStr}`
}

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatCurrency(val: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val)
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

function buildAskQuestion(type: 'daily' | 'weekly', start: string): string {
  const label = parseDate(start).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  if (type === 'daily') {
    return `What happened in retail on ${label}?`
  }

  return `What happened in retail the week of ${label}?`
}

function LatestDailyCard({ briefing }: { briefing: Briefing }) {
  const [, navigate] = useLocation()
  const m = briefing.metadata || {}
  // Prefer total_dtc_revenue (all channels: Shopify + Amazon + TikTok + Walmart)
  // Fall back to shopify_revenue for older briefings that predate the fix
  const shopifyRevenue = toNumber(m.total_dtc_revenue ?? m.shopify_revenue)
  const shopifyOrders = toNumber(m.total_dtc_orders ?? m.shopify_orders)
  const trendDodRevenue = toNumber(m.trend_dod_revenue)
  const trendWowRevenue = toNumber(m.trend_wow_revenue)
  const askQuestion = buildAskQuestion('daily', briefing.date_range_start)

  return (
    <div
      onClick={() => navigate(`/intelligence/${briefing.id}`)}
      className="bg-white border-2 border-blue-200 rounded-2xl p-6 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-xl">
            <Newspaper className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <span className="inline-block px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
              Daily Briefing
            </span>
            <p className="text-sm text-gray-500 mt-1">
              {formatDateRange(briefing.date_range_start, briefing.date_range_end)}
            </p>
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {shopifyRevenue !== null && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-1.5 mb-1">
              <DollarSign className="w-3.5 h-3.5 text-green-600" />
              <span className="text-xs text-gray-500">Revenue</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{formatCurrency(shopifyRevenue)}</div>
          </div>
        )}
        {shopifyOrders !== null && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-1.5 mb-1">
              <ShoppingCart className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-xs text-gray-500">Orders</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{shopifyOrders.toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Trend indicators */}
      {trendDodRevenue !== null && (
        <div className="flex items-center space-x-3 text-sm">
          {trendDodRevenue >= 0 ? (
            <span className="flex items-center text-green-600">
              <TrendingUp className="w-3.5 h-3.5 mr-1" />+{trendDodRevenue.toFixed(1)}% DoD
            </span>
          ) : (
            <span className="flex items-center text-red-600">
              <TrendingDown className="w-3.5 h-3.5 mr-1" />{trendDodRevenue.toFixed(1)}% DoD
            </span>
          )}
          {trendWowRevenue !== null && (
            <span className={trendWowRevenue >= 0 ? 'text-green-600' : 'text-red-600'}>
              {trendWowRevenue >= 0 ? '+' : ''}{trendWowRevenue.toFixed(1)}% WoW
            </span>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          navigate(`/agent-hub?q=${encodeURIComponent(askQuestion)}&submit=true`)
        }}
        className="mt-4 text-xs text-gray-500 hover:text-indigo-600"
      >
        Ask about this
      </button>
    </div>
  )
}

function LatestWeeklyCard({ briefing }: { briefing: Briefing }) {
  const [, navigate] = useLocation()
  const m = briefing.metadata || {}
  const weekRevenueDtc = toNumber(m.week_revenue_dtc)
  const wowRevenueChange = toNumber(m.wow_revenue_change)
  const retailersActive = toStringArray(m.retailers_active)
  const askQuestion = buildAskQuestion('weekly', briefing.date_range_start)

  return (
    <div
      onClick={() => navigate(`/intelligence/${briefing.id}`)}
      className="bg-white border-2 border-purple-200 rounded-2xl p-6 cursor-pointer hover:shadow-lg hover:border-purple-300 transition-all group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-purple-100 rounded-xl">
            <BookOpen className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <span className="inline-block px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full">
              Weekly Digest
            </span>
            <p className="text-sm text-gray-500 mt-1">
              {formatDateRange(briefing.date_range_start, briefing.date_range_end)}
            </p>
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 transition-colors" />
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {weekRevenueDtc !== null && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">DTC Revenue</div>
            <div className="text-lg font-bold text-gray-900">{formatCurrency(weekRevenueDtc)}</div>
          </div>
        )}
        {wowRevenueChange !== null && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">WoW Change</div>
            <div className={`text-lg font-bold ${wowRevenueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {wowRevenueChange >= 0 ? '+' : ''}{wowRevenueChange.toFixed(1)}%
            </div>
          </div>
        )}
      </div>

      {/* Active retailers */}
      {retailersActive.length > 0 && (
        <div className="text-sm text-gray-600">
          <span className="font-medium">{retailersActive.length} active retailers:</span>{' '}
          {retailersActive.join(', ')}
        </div>
      )}

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          navigate(`/agent-hub?q=${encodeURIComponent(askQuestion)}&submit=true`)
        }}
        className="mt-4 text-xs text-gray-500 hover:text-indigo-600"
      >
        Ask about this
      </button>
    </div>
  )
}

function BriefingRow({ briefing }: { briefing: BriefingListItem }) {
  const [, navigate] = useLocation()
  const isDaily = briefing.briefing_type === 'daily'
  const m = briefing.metadata || {}
  // Prefer total_dtc_revenue (all channels) over shopify-only for older briefings
  const shopifyRevenue = toNumber(m.total_dtc_revenue ?? m.shopify_revenue)
  const weekRevenueDtc = toNumber(m.week_revenue_dtc)
  const askQuestion = buildAskQuestion(briefing.briefing_type, briefing.date_range_start)

  return (
    <div
      onClick={() => navigate(`/intelligence/${briefing.id}`)}
      className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
    >
      <div className="flex items-center space-x-4">
        <span
          className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${
            isDaily
              ? 'bg-blue-100 text-blue-700'
              : 'bg-purple-100 text-purple-700'
          }`}
        >
          {isDaily ? 'Daily' : 'Weekly'}
        </span>
        <div>
          <div className="font-medium text-gray-900 text-sm">
            {formatDateRange(briefing.date_range_start, briefing.date_range_end)}
          </div>
          <div className="text-xs text-gray-500">
            Generated {formatTimestamp(briefing.generated_at)}
          </div>
        </div>
      </div>

      {/* Metric preview */}
      <div className="flex items-center space-x-6">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            navigate(`/agent-hub?q=${encodeURIComponent(askQuestion)}&submit=true`)
          }}
          className="text-xs text-gray-500 hover:text-indigo-600"
        >
          Ask about this
        </button>
        {isDaily && shopifyRevenue !== null && (
          <div className="text-right">
            <div className="text-sm font-semibold text-gray-900">{formatCurrency(shopifyRevenue)}</div>
            <div className="text-xs text-gray-500">Revenue</div>
          </div>
        )}
        {!isDaily && weekRevenueDtc !== null && (
          <div className="text-right">
            <div className="text-sm font-semibold text-gray-900">{formatCurrency(weekRevenueDtc)}</div>
            <div className="text-xs text-gray-500">DTC Revenue</div>
          </div>
        )}
        <ArrowRight className="w-4 h-4 text-gray-400" />
      </div>
    </div>
  )
}

export default function IntelligenceHub() {
  const [, navigate] = useLocation()
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [limit] = useState(20)
  const [offset, setOffset] = useState(0)
  const [accumulated, setAccumulated] = useState<BriefingListItem[]>([])

  const typeFilter = activeTab === 'all' ? undefined : activeTab
  const {
    data: listData,
    isLoading: listLoading,
    error: listError,
  } = useBriefings(typeFilter, limit, offset)
  const { data: latestData, isLoading: latestLoading } = useLatestBriefings()

  // Accumulate briefings across pages
  useEffect(() => {
    if (!listData?.briefings) return
    if (listData.offset === 0) {
      // Fresh load or tab change: replace
      setAccumulated(listData.briefings)
      return
    }

    // Load more: append unique rows by id
    setAccumulated((prev) => {
      const seen = new Set(prev.map((item) => item.id))
      const next = listData.briefings.filter((item) => !seen.has(item.id))
      return [...prev, ...next]
    })
  }, [listData])

  const latestDaily = latestData?.briefings.find(b => b.briefing_type === 'daily')
  const latestWeekly = latestData?.briefings.find(b => b.briefing_type === 'weekly')

  const briefings = accumulated
  const total = listData?.total || 0
  const hasMore = offset + limit < total

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'daily', label: 'Daily Briefings' },
    { key: 'weekly', label: 'Weekly Digests' },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader
        title="Intelligence Hub"
        subtitle="Daily Briefings & Weekly Digests"
      />

      <div className="max-w-5xl mx-auto px-8 py-6 space-y-6">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => navigate('/agent-hub')}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Ask PIQ a question
          </button>
        </div>

        {/* Latest cards */}
        {latestLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[0, 1].map(i => (
              <div key={i} className="bg-white border border-gray-200 rounded-2xl p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
                <div className="h-8 bg-gray-200 rounded w-1/2 mb-3" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (latestDaily || latestWeekly) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {latestDaily && <LatestDailyCard briefing={latestDaily} />}
            {latestWeekly && <LatestWeeklyCard briefing={latestWeekly} />}
          </div>
        ) : null}

        {/* Filter tabs */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setOffset(0); setAccumulated([]); }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Timeline list */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {listLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : listError ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Calendar className="w-12 h-12 mb-3 text-gray-300" />
              <p className="text-lg font-medium text-gray-900">Unable to load briefings</p>
              <p className="text-sm">{listError instanceof Error ? listError.message : 'Please try again.'}</p>
            </div>
          ) : briefings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Calendar className="w-12 h-12 mb-3 text-gray-300" />
              <p className="text-lg font-medium">No briefings yet</p>
              <p className="text-sm">Briefings will appear here once generated</p>
            </div>
          ) : (
            <>
              {briefings.map(b => (
                <BriefingRow key={b.id} briefing={b} />
              ))}
            </>
          )}
        </div>

        {/* Load more */}
        {hasMore && (
          <div className="flex justify-center">
            <button
              onClick={() => setOffset(o => o + limit)}
              className="flex items-center space-x-2 px-6 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ChevronDown className="w-4 h-4" />
              <span>Load more</span>
            </button>
          </div>
        )}

        {/* Total count */}
        {total > 0 && (
          <p className="text-center text-sm text-gray-500">
            Showing {briefings.length} of {total} briefings
          </p>
        )}
      </div>
    </div>
  )
}
